// src/pages/DailyUpdates.jsx - Fixed with proper API calls

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  FiMessageSquare,
  FiCalendar,
  FiSearch,
  FiUser,
  FiCheckCircle,
  FiClock,
  FiAlertCircle,
  FiSend,
  FiRefreshCw,
  FiChevronDown,
  FiChevronUp,
  FiX,
  FiEye,
  FiEyeOff,
  FiUsers,
  FiTrendingUp,
  FiTrendingDown,
  FiActivity
} from 'react-icons/fi';
import { api } from '../services/api';
import { getCurrentUser } from '../services/authService';
import { useAppContext } from '../context/AppContext';

const DailyUpdates = () => {
  const { refreshData } = useAppContext();
  const currentUser = getCurrentUser();
  const isAdminOrHR = currentUser?.role === 'admin' || currentUser?.role === 'hr_manager';

  // State
  const [employees, setEmployees] = useState([]);
  const [dailyUpdates, setDailyUpdates] = useState([]);
  const [filteredUpdates, setFilteredUpdates] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7));
  const [selectedEmployee, setSelectedEmployee] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(true);
  const [expandedEmployee, setExpandedEmployee] = useState(null);
  const [lastApiCall, setLastApiCall] = useState(null);
  
  // Use ref to track if this is the first load
  const isFirstLoad = useRef(true);
  
  // Stats
  const [stats, setStats] = useState({
    totalEmployees: 0,
    totalUpdates: 0,
    employeesUpdated: 0,
    pendingEmployees: 0,
    todayUpdates: 0
  });

  // ========== LOAD DATA WITH BACKEND FILTERS ==========
  const loadData = useCallback(async (monthOverride, employeeOverride, searchOverride) => {
    // Use override values if provided, otherwise use state values
    const currentMonth = monthOverride || selectedMonth;
    const currentEmployee = employeeOverride || selectedEmployee;
    const currentSearch = searchOverride || searchTerm;
    
 

    
    setDataLoading(true);
    setLoading(true);
    
    try {
      // ========== STEP 1: Fetch REAL employees from database ==========
      const employeesRes = await api.get('/api/employees/');
      let employeeList = [];
      if (Array.isArray(employeesRes)) {
        employeeList = employeesRes;
      } else if (employeesRes.results && Array.isArray(employeesRes.results)) {
        employeeList = employeesRes.results;
      }
      
      // Filter out invalid employees
      const validEmployees = employeeList.filter(emp => 
        emp.name && emp.name.trim() !== '' && emp.name !== 'Unknown'
      );
      setEmployees(validEmployees);

      // Create a Set of valid employee IDs for quick lookup
      const validEmployeeIds = new Set(validEmployees.map(emp => emp.employee_id));

      // ========== STEP 2: Build query parameters for backend ==========
      const params = new URLSearchParams();
      
      // Send the selected month to backend (CRITICAL)
      if (currentMonth && currentMonth !== 'all') {
        params.append('month', currentMonth);
      }
      
      if (currentEmployee && currentEmployee !== 'all') {
        params.append('employee', currentEmployee);
      }
      
      if (currentSearch && currentSearch.trim()) {
        params.append('search', currentSearch.trim());
      }
      
      const queryString = params.toString();
      const url = `/api/performance/daily-updates/hr_dashboard/${queryString ? `?${queryString}` : ''}`;
      
      setLastApiCall(url);

      // ========== STEP 3: Fetch data with filters ==========
      const updatesRes = await api.get(url);
      const updatesData = Array.isArray(updatesRes) ? updatesRes : (updatesRes.results || []);
      

      // ========== STEP 4: Only keep real employees ==========
      const filteredData = updatesData.filter(emp => 
        validEmployeeIds.has(emp.employee_id)
      );
      


      // ========== STEP 5: Set data ==========
      setDailyUpdates(filteredData);
      setFilteredUpdates(filteredData);
      
      // Calculate stats
      calculateStats(filteredData,validEmployees);
      
    } catch (error) {
      
      // ========== Fallback: Try to get from daily endpoint ==========
      try {
        const params = new URLSearchParams();
        if (currentMonth && currentMonth !== 'all') {
          params.append('month', currentMonth);
        }
        if (currentEmployee && currentEmployee !== 'all') {
          params.append('employee_id', currentEmployee);
        }
        
        const queryString = params.toString();
        const fallbackUrl = `/api/performance/daily/${queryString ? `?${queryString}` : ''}`;
        
        const fallbackRes = await api.get(fallbackUrl);
        const fallbackData = Array.isArray(fallbackRes) ? fallbackRes : (fallbackRes.results || []);
        
        
        // Get valid employee IDs for filtering
        const validEmployeeIds = new Set(employees.map(emp => emp.employee_id));
        
        // Group by employee - ONLY REAL employees
        const grouped = fallbackData.reduce((acc, item) => {
          const empId = item.employee_id;
          if (!validEmployeeIds.has(empId)) return acc; // Skip fake employees
          
          if (!acc[empId]) {
            const emp = employees.find(e => e.employee_id === empId);
            acc[empId] = {
              employee_id: empId,
              employee_name: emp?.name || 'Unknown',
              department: emp?.department || 'N/A',
              daily_updates: []
            };
          }
          acc[empId].daily_updates.push({
            id: item.id,
            date: item.date,
            completed_amount: parseFloat(item.completed_amount) || 0,
            tasks: item.tasks || [],
            task_description: item.task_description || '',
            status: item.status || 'Pending'
          });
          return acc;
        }, {});
        
        const groupedArray = Object.values(grouped);
        setDailyUpdates(groupedArray);
        setFilteredUpdates(groupedArray);
        calculateStats(groupedArray);
      } catch (fallbackError) {
        setDailyUpdates([]);
        setFilteredUpdates([]);
        setStats({
          totalEmployees: 0,
          totalUpdates: 0,
          employeesUpdated: 0,
          pendingEmployees: 0,
          todayUpdates: 0
        });
      }
    } finally {
      setDataLoading(false);
      setLoading(false);
      isFirstLoad.current = false;
    }
  }, [selectedMonth, selectedEmployee, searchTerm, employees]);

  // ========== Apply additional filters (search, employee) ==========
  const applyFilters = useCallback((data) => {
    let filtered = [...data];

    // Employee filter is already applied in backend, but we keep it for safety
    if (selectedEmployee !== 'all') {
      filtered = filtered.filter(emp => emp.employee_id === selectedEmployee);
    }

    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(emp =>
        emp.employee_name?.toLowerCase().includes(term) ||
        emp.employee_id?.toLowerCase().includes(term) ||
        emp.department?.toLowerCase().includes(term)
      );
    }

    setFilteredUpdates(filtered);
  }, [selectedEmployee, searchTerm]);

  // ========== Calculate Statistics ==========
  const calculateStats = useCallback((data, employeeList) => {
    const totalEmployees = employeeList.length;
    const today = new Date().toISOString().slice(0, 10);

    // Total updates in selected month
    const totalUpdates = data.reduce(
        (sum, emp) => sum + (emp.daily_updates?.length || 0),
        0
    );

    // Employees who updated TODAY
    const employeesUpdated = data.filter(emp =>
        emp.daily_updates?.some(update => update.date === today)
    ).length;

    // Pending employees TODAY
    const pendingEmployees = totalEmployees - employeesUpdated;

    // Number of today's update records
    const todayUpdates = data.reduce(
        (sum, emp) =>
            sum +
            (emp.daily_updates?.filter(update => update.date === today).length || 0),
        0
    );

    setStats({
        totalEmployees,
        totalUpdates,
        employeesUpdated,
        pendingEmployees,
        todayUpdates
    });
}, []);
  // ========== Effects ==========
  
  // Load data on mount
  useEffect(() => {
    loadData();
  }, []);

  // Reload when month changes
  useEffect(() => {
    if (!isFirstLoad.current) {
      loadData();
    }
  }, [selectedMonth]);

  // Reload when employee filter changes
  useEffect(() => {
    if (!isFirstLoad.current) {
      loadData();
    }
  }, [selectedEmployee]);

  // Apply search/employee filters when data changes
  useEffect(() => {
    applyFilters(dailyUpdates);
  }, [dailyUpdates, selectedEmployee, searchTerm, applyFilters]);

  // Toggle employee expansion
  const toggleEmployee = (employeeId) => {
    setExpandedEmployee(expandedEmployee === employeeId ? null : employeeId);
  };

  // Check if a date is today
  const isToday = (dateString) => {
    const today = new Date().toISOString().slice(0, 10);
    return dateString === today;
  };

  // Status badge - Completed if description OR amount > 0
  const getStatusBadge = (update) => {
    const hasDescription = update.task_description && update.task_description.trim().length > 0;
    const hasAmount = update.completed_amount > 0;
    
    if (hasDescription || hasAmount) {
      return <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-700">Completed</span>;
    }
    return <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-700">Pending</span>;
  };

  // Get unique employees for filter
  const employeesWithUpdates = dailyUpdates.filter(emp => emp.daily_updates?.length > 0);

  // Handle month change
  const handleMonthChange = (e) => {
    const newMonth = e.target.value;

    setSelectedMonth(newMonth);
    // loadData will be triggered by useEffect
  };

  // Handle employee change
  const handleEmployeeChange = (e) => {
    const newEmployee = e.target.value;
    setSelectedEmployee(newEmployee);
    // loadData will be triggered by useEffect
  };

  // Handle search
  const handleSearchChange = (e) => {
    const newSearch = e.target.value;
    setSearchTerm(newSearch);
    // applyFilters will be triggered by useEffect
  };

  // Handle refresh
  const handleRefresh = () => {
    loadData();
    if (refreshData) refreshData();
  };

  // Loading state
  if (dataLoading && isFirstLoad.current) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
          <p className="text-gray-500">Loading daily updates...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="animate-fade-in">
      {/* Page Header */}
      <div className="mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-800 flex items-center gap-3">
              <FiMessageSquare className="text-indigo-600" />
              Daily Employee Updates
            </h1>
            <p className="text-xs text-blue-600 mt-1">
             
            </p>
            <p className="text-xs text-green-600 mt-1">
            </p>
            {lastApiCall && (
              <p className="text-xs text-gray-400 mt-1 truncate max-w-md">
              </p>
            )}
          </div>
          <button
            onClick={handleRefresh}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition flex items-center gap-2"
          >
            <FiRefreshCw size={16} className={loading ? 'animate-spin' : ''} /> Refresh
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-6">
        <div className="stat-card bg-gradient-to-br from-indigo-50 to-indigo-100 border-indigo-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 font-medium">Total Employees</p>
              <p className="text-2xl font-bold text-indigo-700 mt-1">{stats.totalEmployees}</p>
            </div>
            <div className="w-12 h-12 bg-indigo-200 rounded-xl flex items-center justify-center">
              <FiUsers className="text-indigo-700 text-xl" />
            </div>
          </div>
        </div>

        <div className="stat-card bg-gradient-to-br from-green-50 to-green-100 border-green-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 font-medium">Total Updates</p>
              <p className="text-2xl font-bold text-green-700 mt-1">{stats.totalUpdates}</p>
              <p className="text-xs text-green-600 mt-1">
                {stats.employeesUpdated} employees updated
              </p>
            </div>
            <div className="w-12 h-12 bg-green-200 rounded-xl flex items-center justify-center">
              <FiMessageSquare className="text-green-700 text-xl" />
            </div>
          </div>
        </div>

        <div className="stat-card bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 font-medium">Employees Updated</p>
              <p className="text-2xl font-bold text-blue-700 mt-1">{stats.employeesUpdated}</p>
              <p className="text-xs text-blue-600 mt-1">
                {stats.totalEmployees > 0 ? Math.round((stats.employeesUpdated / stats.totalEmployees) * 100) : 0}% of total
              </p>
            </div>
            <div className="w-12 h-12 bg-blue-200 rounded-xl flex items-center justify-center">
              <FiActivity className="text-blue-700 text-xl" />
            </div>
          </div>
        </div>

        <div className="stat-card bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 font-medium">Pending Employees</p>
              <p className="text-2xl font-bold text-orange-700 mt-1">{stats.pendingEmployees}</p>
              <p className="text-xs text-orange-600 mt-1">
                {stats.todayUpdates} updates today
              </p>
            </div>
            <div className="w-12 h-12 bg-orange-200 rounded-xl flex items-center justify-center">
              <FiClock className="text-orange-700 text-xl" />
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 mb-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search by employee name or ID..."
              value={searchTerm}
              onChange={handleSearchChange}
              className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-xl focus:outline-none focus:border-indigo-400 bg-white"
            />
          </div>
          <div className="w-full sm:w-56 relative">
            <FiUser className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <select
              value={selectedEmployee}
              onChange={handleEmployeeChange}
              className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-xl focus:outline-none focus:border-indigo-400 bg-white appearance-none"
            >
              <option value="all">All Employees</option>
              {employeesWithUpdates.map(emp => (
                <option key={emp.employee_id} value={emp.employee_id}>
                  {emp.employee_name} ({emp.employee_id})
                </option>
              ))}
            </select>
          </div>
          <div className="w-full sm:w-56 relative">
            <FiCalendar className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="month"
              value={selectedMonth}
              onChange={handleMonthChange}
              className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-xl focus:outline-none focus:border-indigo-400 bg-white"
            />
          </div>
        </div>
      </div>

      {/* Daily Updates List */}
      {dailyUpdates.length === 0 ? (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-12 text-center">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <FiMessageSquare className="text-gray-400 text-2xl" />
          </div>
          <p className="text-gray-500">No updates found for {selectedMonth}</p>
         
          
        </div>
      ) : filteredUpdates.length === 0 ? (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-12 text-center">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <FiSearch className="text-gray-400 text-2xl" />
          </div>
          <p className="text-gray-500">No matching employees found</p>
          <p className="text-sm text-gray-400 mt-1">Try adjusting your search or filters</p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredUpdates.map((employee) => {
            const isExpanded = expandedEmployee === employee.employee_id;
            const updates = employee.daily_updates || [];

            // Check if employee has today's update
            const hasTodayUpdate = updates.some(u => isToday(u.date));

            // Check if employee has any update (description OR amount > 0)
            const hasAnyUpdate = updates.length > 0;

            return (
              <div
                key={employee.employee_id}
                className={`bg-white rounded-2xl shadow-sm border overflow-hidden transition-all ${
                  hasTodayUpdate 
                    ? 'border-indigo-400 shadow-md ring-2 ring-indigo-200' 
                    : hasAnyUpdate
                      ? 'border-green-300 hover:shadow-md'
                      : 'border-gray-100 hover:shadow-md'
                }`}
              >
                {/* Employee Header */}
                <div
                  className={`flex items-center justify-between p-4 cursor-pointer transition ${
                    hasTodayUpdate ? 'bg-indigo-50 hover:bg-indigo-100' : 
                    hasAnyUpdate ? 'bg-green-50 hover:bg-green-100' :
                    'hover:bg-gray-50'
                  }`}
                  onClick={() => toggleEmployee(employee.employee_id)}
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold ${
                      hasTodayUpdate 
                        ? 'bg-gradient-to-br from-indigo-500 to-indigo-700' 
                        : hasAnyUpdate
                          ? 'bg-gradient-to-br from-green-500 to-green-700'
                          : 'bg-gradient-to-br from-gray-400 to-gray-500'
                    }`}>
                      {employee.employee_name?.charAt(0) || 'U'}
                    </div>
                    <div>
                      <p className="font-semibold text-gray-800">{employee.employee_name}</p>
                      <p className="text-xs text-gray-500">{employee.employee_id} • {employee.department}</p>
                      {hasTodayUpdate && (
                        <span className="text-xs text-indigo-600 font-medium flex items-center gap-1 mt-0.5">
                          <FiCheckCircle size={12} /> Updated today
                        </span>
                      )}
                      {hasAnyUpdate && !hasTodayUpdate && (
                        <span className="text-xs text-green-600 font-medium flex items-center gap-1 mt-0.5">
                          <FiCheckCircle size={12} /> {updates.length} update{updates.length > 1 ? 's' : ''}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-3 text-sm text-gray-500">
                      <span className="flex items-center gap-1">
                        <FiCheckCircle className={hasAnyUpdate ? 'text-green-500' : 'text-gray-300'} />
                        {updates.length}
                      </span>
                      {hasTodayUpdate && (
                        <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-indigo-600 text-white">
                          Today
                        </span>
                      )}
                    </div>
                    {isExpanded ? <FiChevronUp size={20} /> : <FiChevronDown size={20} />}
                  </div>
                </div>

                {/* Employee Updates */}
                {isExpanded && (
                  <div className="border-t border-gray-100 p-4">
                    <div className="space-y-3">
                      {updates.map((update) => {
                        const isTodayUpdate = isToday(update.date);
                        const hasDescription = update.task_description && update.task_description.trim().length > 0;
                        const hasAmount = update.completed_amount > 0;
                        
                        return (
                          <div
                            key={update.id || update.date}
                            className={`rounded-xl p-4 transition ${
                              isTodayUpdate 
                                ? 'bg-indigo-50 border-2 border-indigo-400 shadow-md' 
                                : hasDescription || hasAmount
                                  ? 'bg-green-50 border border-green-200'
                                  : 'bg-gray-50 hover:bg-gray-100'
                            }`}
                          >
                            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
                              <div className="flex-1">
                                <div className="flex items-center gap-3 mb-2 flex-wrap">
                                  <span className={`text-sm font-medium ${
                                    isTodayUpdate ? 'text-indigo-700' : 'text-gray-600'
                                  }`}>
                                    {new Date(update.date).toLocaleDateString('en-IN', {
                                      weekday: 'short',
                                      day: 'numeric',
                                      month: 'short',
                                      year: 'numeric'
                                    })}
                                  </span>
                                  {isTodayUpdate && (
                                    <span className="px-2 py-1 text-xs font-medium rounded-full bg-indigo-600 text-white animate-pulse">
                                      Today
                                    </span>
                                  )}
                                  {getStatusBadge(update)}
                                  {hasDescription && (
                                    <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-700">
                                      Has Description
                                    </span>
                                  )}
                                  {hasAmount && (
                                    <span className="px-2 py-1 text-xs font-medium rounded-full bg-purple-100 text-purple-700">
                                      Has Amount
                                    </span>
                                  )}
                                </div>
                                
                                {update.task_description && (
                                  <p className="text-gray-700 whitespace-pre-wrap">
                                    {update.task_description}
                                  </p>
                                )}
                                {update.completed_amount > 0 && (
                                  <p className="text-sm text-green-600 font-medium mt-1">
                                    Completed: ₹{Number(update.completed_amount).toLocaleString()}
                                  </p>
                                )}
                                {update.tasks && update.tasks.length > 0 && (
                                  <div className="mt-2 flex flex-wrap gap-1">
                                    {update.tasks.map((task, idx) => (
                                      <span key={idx} className="px-2 py-0.5 text-xs bg-gray-200 rounded-full text-gray-600">
                                        {task}
                                      </span>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default DailyUpdates;